import React, { useContext, useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Divider, Dropdown, Form, Icon, Input, Label } from 'semantic-ui-react';
import { DateInput } from 'semantic-ui-calendar-react';
import AuthService, { SendSmsGetDataType } from '~/api/services/auth';
import { FormFields } from '~/components/FormFields';
import notify from '~/components/Notify';
import { RouteTypes } from '~/routes/constants';
import { ThemeContext } from 'styled-components';
import LayoutAuth from './LayoutAuth';
import { El } from './style';
import { MANAGER_ROLE_SLUG, passwordRegexWithoutSpecChar } from '~/Constants';
import dayjs from 'dayjs';
import i18n from '~/components/i18n';
import { handleErrors, parseDate } from '~/utils/helpers';
import TwoFaField from './TwoFaField';
import { Nullable } from '~/types';

const passwordRegex = /^(?=.*[0-9])(?=.*[^\p{L}\d])(?=.*[A-Z])(?=.*[a-z])/u
const MINIMUM_LENGTH_PASSWORD = 8;
const MAX_LENGTH_PASSWORD = 32;
const swissCountriesIso = ['ch', 'de', 'lu']
interface PasswordFormProps {
  resetPassword?: boolean
}

interface ResetPasswordDataType {
  passwordSecurity: boolean
  tfaType: string
  mobileNumber?: string
  companyName?: string
  supplierNo: string
  roleSlug: string
  agisId: Nullable<string>
  name?: string
}

const PasswordForm: React.FC<PasswordFormProps> = (props) => {
  const { resetPassword } = props
  const { t } = useTranslation()
  const { code } = useParams();
  const navigate = useNavigate()
  const { logoTitle, firstColor, chEnv } = useContext(ThemeContext)
  const isSwissDairy = chEnv === 1
  const settingsData = useContext(ThemeContext)
  const matchPasswordErrorMessage = t('Passwords do not match');

  const [isBusy, setIsBusy] = useState(false)
  const [sendingTwoFaCode, setSendingTwoFaCode] = useState(false)
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [visibleRepeatPassword, setVisibleRepeatPassword] = useState(false);
  const [registrationInfo, setRegistrationInfo] = useState<SendSmsGetDataType>(null)
  const [registrationInfoReset, setRegistrationInfoReset] = useState<ResetPasswordDataType>(null)
  const [tfaType, setTfaType] = useState('')
  const [mobileNumber, setMobileNumber] = useState<string | number | boolean | (string | number | boolean)[]>('')
  const [mobilePrefix, setMobilePrefix] = useState<string | number | boolean | (string | number | boolean)[]>('')
  const [correctTfaCode, setCorrectTfaCode] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  const { handleSubmit, control, getValues, watch, setValue } = useForm({
    mode: 'onChange'
  });

  const watchTermsAccepted = watch('terms_accepted')
  const watchPassword = watch('password')
  const watchPasswordConfirmation = watch('passwordConfirmation')

  useEffect(() => {
    checkLinkToVerifyUser()
  }, [])

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  useEffect(() => {
    if(tfaType && tfaType !== 'google') {
      setResendCooldown(30)
    }
  }, [tfaType])

  function getCorrectLanguageCode(languageCode) {
    switch (languageCode) {
      case 'gb':
        return 'en'
      case 'fr_BE':
      case 'fr_CH':
        return 'fr'
      case 'nl_BE':
        return 'nl'
      case 'de_CH':
        return 'de'
      default:
        return languageCode
    }
  }

  async function checkLinkToVerifyUser() {
    setIsBusy(true);
    try {
      let response;
      const languageStored = localStorage.getItem('i18nextLng') || 'de'
      let responseData
      if (!resetPassword) {
        response = await AuthService.setPasswordCheck({ code });
        responseData = await AuthService.sendSmsGetData({ code })
        dayjs.locale(responseData.data?.user?.languageCode || languageStored)
        i18n.changeLanguage(responseData.data?.user?.languageCode || languageStored)
        const defaultCountry = isSwissDairy ? 'ch' : 'de'
        const deaultMobilePrefix = responseData.data?.countries?.find(country => country?.iso === defaultCountry)
        setMobilePrefix(deaultMobilePrefix?.id)
        setRegistrationInfo(responseData.data)
        setTfaType(responseData.data.user.tfaType)
        if(responseData.data.user?.roleSlug === MANAGER_ROLE_SLUG) {
          setValue('birthDate', '')
        }
      } else {
        response = await AuthService.resetPasswordCheck({ code });
        responseData = await AuthService.resetPasswordData({ code })
        if(responseData.data?.tfaType === 'sms' && responseData.data?.mobileNumber) {
          notify(t('You have just received a PIN code to your mobile number {{mobileNumber}}.', { mobileNumber: responseData?.data?.mobileNumber }), 'success')
        }
        dayjs.locale(responseData.data?.languageCode || languageStored)
        i18n.changeLanguage(responseData.data?.languageCode || languageStored)
        setRegistrationInfoReset(responseData.data)
        setTfaType(responseData.data.tfaType)
      }

      if (response.data.message) {
        navigate(RouteTypes.LOGIN)
        notify(t('Set link password expired'), 'error');
      }
    } catch (error) {
      navigate(RouteTypes.LOGIN)
      notify(t('Set link password expired'), 'error');
    }
    setIsBusy(false);
  }

  async function onSubmitPassword(data) {
    data.code = code;
    data.password = data?.password?.toString().trim()
    data.passwordConfirmation = data?.passwordConfirmation?.toString().trim()
    setIsBusy(true);
    try {
      if (!resetPassword) {
        if(data?.birthDate) {
          data.birthDate = parseDate(data.birthDate)
        }
        await AuthService.setPassword(data);
      } else {
        await AuthService.resetPassword(data);
      }
      navigate(RouteTypes.LOGIN);
      notify(t('Password set'), 'success');
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderCheckTerms() {
    const stackedLabel = (
      <span>
        {settingsData?.files?.map((file, index) => (
          <span key={index} style={{ display: 'block' }}>{file.name}</span>
        ))}
      </span>
    )
    return <FormFields.Checkbox
      label={stackedLabel}
      control={control}
      name={'terms_accepted'}
      rules={{
        required: {
          value: true,
          message: (t('Please accept terms of use'))
        }
      }}
      defaultValue={0}
      checkboxValue={1}
    />
  }

  async function sendCodeWithMobile() {
    setSendingTwoFaCode(true)
    try {
      await AuthService.saveMobileNumber({
        mobileNumber,
        mobilePrefix,
        code
      })
      notify(t('Code was sent'), 'success')
      setResendCooldown(30)
    } catch (error) {
      handleErrors(error)
    }
    setSendingTwoFaCode(false)
  }

  async function handleResendCode() {
    setSendingTwoFaCode(true);
    try {
      if (!resetPassword) {
        await AuthService.resendSetPasswordCode({ code });
      } else {
        await AuthService.resendResetPasswordCode({ code });
      }
      notify(t('Code was sent'), 'success');
      setResendCooldown(30)
    } catch (error) {
      handleErrors(error)
    }
    setSendingTwoFaCode(false);
  }

  function getVerificationLabel() {
    switch (tfaType) {
      case 'email':
        return t('E-Mail')
      case 'sms':
        return t('SMS')
      case 'google':
        return t('Authenticator')
      default:
        return ''
    }
  }

  function renderNewMobileNumber() {
    const countries = isSwissDairy ? registrationInfo?.countries?.filter(country => swissCountriesIso.find(swissCountryIso => swissCountryIso === country.iso)) :
      registrationInfo?.countries || []

    return <Form.Field style={{ paddingLeft: 0 }} required>
      <label>{t('Mobile number')}</label>
      <Input
        style={{ minWidth: 250 }}
        value={mobileNumber}
        label={<Dropdown
          scrolling
          basic
          options={countries?.map(country => ({ value: country.id, text: country.phoneCode, flag: country.iso }))}
          selectOnBlur={false}
          onChange={(e, data) => setMobilePrefix(data.value)}
          value={mobilePrefix || ''}
          placeholder={t('Code')}
        />}
        onChange={(e, data) => setMobileNumber(data.value)}
        labelPosition='left'
        placeholder={mobilePrefix ? t('Mobile number') : t('Please select mobile code')}
        type={'number'}
        name={'mobileNumber'}
      />
    </Form.Field>
  }

  function checkIfMobileIsEntered(noPredefinedNumber: boolean) {
    if (noPredefinedNumber) {
      // since you cannot have correct two fa code without entering mobile phone and prefix
      // correctTfa code is here enough to validate
      return !correctTfaCode
    }
    return false
  }

  function renderUserDetails() {
    return <Form.Group >
      <Form.Field width={8}>
        <label>{t('First name:')}</label>
        <El.StyledLabelInfo>
          <Icon name='user' />
          <Label.Detail>{`${registrationInfo?.user.firstName}`}</Label.Detail>
        </El.StyledLabelInfo>
      </Form.Field>
      <Form.Field width={8}>
        <label>{t('Last name:')}</label>
        <El.StyledLabelInfo >
          <Icon name='user' />
          <Label.Detail>{`${registrationInfo?.user.lastName}`}</Label.Detail>
        </El.StyledLabelInfo>
      </Form.Field>
    </Form.Group>
  }

  function renderDairyDetails() {
    const data = resetPassword ? registrationInfoReset : registrationInfo?.user
    return <>
      <div style={{ textAlign: 'initial' }}>
        <Form.Group>
          <Form.Field width={8}>
            <label>{t('Supplier no.')}</label>
            <El.StyledLabelInfo>
              <Icon name='factory' />
              <Label.Detail>{`${data?.supplierNo || ''}`}</Label.Detail>
            </El.StyledLabelInfo>
          </Form.Field>
          <Form.Field width={8}>
            <label>{t('Member')}</label>
            <El.StyledLabelInfo>
              <Icon name='address book outline' />
              <Label.Detail>{`${data?.companyName || ''}`}</Label.Detail>
            </El.StyledLabelInfo>
          </Form.Field>
        </Form.Group>
      </div>
      <Divider section style={{ marginBottom: '1rem', color: firstColor }} />
    </>
  }

  function renderActions(enableCodeField, mobileNumberNotPreset) {
    const isCooldownActive = resendCooldown > 0
    const isButtonDisabled = mobileNumberNotPreset
      ? mobileNumber === '' || mobilePrefix === '' || sendingTwoFaCode || isCooldownActive
      : sendingTwoFaCode || isCooldownActive
    const buttonContent = isCooldownActive
      ? `${t('Send 2FA code')} (${resendCooldown}s)`
      : t('Send 2FA code')

    return <Form.Group style={{ justifyContent: 'center', width: '110%' }}>
      {tfaType !== 'google' && enableCodeField && <Form.Field style={El.loginFormSubmitWrapper}>
        <Button
          onClick={() => mobileNumberNotPreset ? sendCodeWithMobile() : handleResendCode()}
          style={El.loginFormSubmitButton}
          type={'button'}
          primary
          loading={sendingTwoFaCode}
          content={buttonContent}
          disabled={isButtonDisabled}
        />
      </Form.Field>}
      <Form.Field style={El.loginFormSubmitWrapper}>
        <Button
          style={El.loginFormSubmitButton}
          primary
          disabled={
            (registrationInfo && settingsData?.files?.length > 0 && !watchTermsAccepted) ||
            checkIfMobileIsEntered(mobileNumberNotPreset)
          }
          content={resetPassword ? t('Reset password') : t('Set')}
        />
      </Form.Field>
    </Form.Group>
  }

  function renderPasswordFields() {
    const specialCharPasswordReq = registrationInfo?.dairySettings.passwordSecurity || registrationInfoReset?.passwordSecurity
    return <>
      <FormFields.Input
        control={control}
        style={El.loginFormField}
        label={t('Assign new password')}
        styleLabel={{ marginTop: '-14px', position: 'relative' }}
        name='password'
        size='large'
        type={visiblePassword ? 'text' : 'password'}
        icon={
          <Icon
            link
            name={visiblePassword ? 'eye slash' : 'eye'}
            onClick={() => setVisiblePassword(!visiblePassword)}
          />
        }
        showOldErrorMessage={Boolean(watchPassword)}
        rules={{
          required: {
            value: true,
            message: t('Please enter password')
          },
          pattern: {
            value: specialCharPasswordReq ? passwordRegex : passwordRegexWithoutSpecChar,
            message: specialCharPasswordReq ?
              t('The password must contain at least one uppercase letter, one lowercase letter, one special character and one number. In addition, the password must be at least 8 characters long.') :
              t('The password must contain at least one uppercase letter, one lowercase letter, and one number. The password must also be at least 8 characters long.')
          },
          minLength: {
            value: MINIMUM_LENGTH_PASSWORD,
            message: t('Password must be at least 8 characters long')
          },
          maxLength: {
            value: MAX_LENGTH_PASSWORD,
            message: t('Max character is 32')
          }
        }}
        placeholder={t('Password')}
      />
      <FormFields.Input
        style={El.loginFormField}
        label={t('Confirm new Password')}
        showOldErrorMessage={Boolean(watchPasswordConfirmation)}
        styleLabel={{ marginTop: '-14px', position: 'relative' }}
        name='passwordConfirmation'
        type={visibleRepeatPassword ? 'text' : 'password'}
        icon={
          <Icon
            link
            name={visibleRepeatPassword ? 'eye slash' : 'eye'}
            onClick={() => setVisibleRepeatPassword(!visibleRepeatPassword)}
          />
        }
        size='large'
        rules={{
          required: {
            value: true,
            message: t('Please enter password confirmation')
          },
          validate: (value) => getValues('password') === value || matchPasswordErrorMessage
        }}
        placeholder={t('Password confirmation')}
        control={control}
      />
    </>
  }

  function renderTwoFa() {
    const verificationLabel = registrationInfo?.dairySettings.smsVerificationRegistration ? t('SMS') : getVerificationLabel()
    return <TwoFaField
      control={control}
      reset={resetPassword}
      keyName={'pinCode'}
      verificationLabel={verificationLabel}
      onChangeCorrectTwoFaCode={(correctTfaCode) => setCorrectTfaCode(correctTfaCode)}
    />
  }

  function renderManagerDetails() {
    const data = resetPassword ? registrationInfoReset : registrationInfo?.user
    return <>
      <div style={{ textAlign: 'initial' }}>
        <Form.Group>
          <Form.Field width={8}>
            <label>{t('AGIS ID Person')}</label>
            <El.StyledLabelInfo>
              <Icon name='factory' />
              <Label.Detail>{`${data?.agisId || ''}`}</Label.Detail>
            </El.StyledLabelInfo>
          </Form.Field>
          <Form.Field width={8}>
            <label>{t('Manager')}</label>
            <El.StyledLabelInfo>
              <Icon name='address book outline' />
              <Label.Detail>{`${data?.name || ''}`}</Label.Detail>
            </El.StyledLabelInfo>
          </Form.Field>
        </Form.Group>
      </div>
      <Divider section style={{ marginBottom: '1rem', color: firstColor }} />
    </>
  }

  function renderDateOfBirth() {
    const maxDate = dayjs().subtract(18, 'year').format('DD.MM.YYYY')
    const minDate = dayjs().subtract(90, 'year').format('DD.MM.YYYY')
    const languageStored = localStorage.getItem('i18nextLng')
    return (
      <Controller
        control={control}
        name='birthDate'
        rules={{
          required: t('Please enter date of birth'),
          validate: (value) => {
            const age = dayjs().diff(dayjs(value, 'DD.MM.YYYY'), 'year')
            if (age < 18) return t('You must be at least 18 years old')
            if (age > 90) return t('Date of birth is not valid')
            return true
          }
        }}
        render={({ field, fieldState }) => (
          <Form.Field width={8} required error={Boolean(fieldState.error)}>
            <label>{t('Date of birth')}</label>
            <DateInput
              name='birthDate'
              placeholder='dd.mm.yyyy'
              value={field.value || ''}
              dateFormat='DD.MM.YYYY'
              iconPosition='left'
              closable
              localization={getCorrectLanguageCode(languageStored)}
              minDate={minDate}
              maxDate={maxDate}
              initialDate={maxDate}
              autoComplete='new-password'
              onChange={(e, { value }) => field.onChange(value)}
            />
          </Form.Field>
        )}
      />
    )
  }

  const renderPasswordForm = () => {
    const enableCodeField = registrationInfo?.dairySettings.smsVerificationRegistration || registrationInfoReset
    const mobileNumberNotPreset = registrationInfo?.user.mobileSet === 0 && registrationInfo?.dairySettings?.smsVerificationRegistration === 1
    const displayNumber = Boolean(registrationInfo?.dairySettings?.showMobileOnVerification) && registrationInfo?.user.mobileSet === 1

    const managerRole = registrationInfo?.user?.roleSlug === MANAGER_ROLE_SLUG || registrationInfoReset?.roleSlug === MANAGER_ROLE_SLUG
    const birthDateVisible = Boolean(registrationInfo?.dairySettings?.birthDateVisible)
    
    return (
      <Form
        style={El.loginForm}
        onSubmit={handleSubmit(onSubmitPassword)}
        autoComplete={'off'}
      >
        <El.FixedPrice style={{ backgroundImage: `url(${logoTitle})` }} />
        <El.FormLogo style={{ color: firstColor }}>{settingsData?.headerTitle || ''}</El.FormLogo>
        <El.FormHeading style={{ marginBottom: '1rem', color: firstColor, fontWeight: 'bold' }}>
          {t('User\'s info')}
        </El.FormHeading>
        <Divider section style={{ marginTop: '1em' }}/>
        {managerRole ? renderManagerDetails() : renderDairyDetails()}
        <div style={{ textAlign: 'initial' }}>
          {registrationInfo?.user.roleSlug && renderUserDetails()}
          <Form.Group>
            {!resetPassword && birthDateVisible && renderDateOfBirth()}
            {displayNumber && <Form.Field width={8} size='large'>
              <label>{t('Mobile number:')}</label>
              <El.StyledLabelInfo>
                <Icon name='phone' />
                <Label.Detail>{`${registrationInfo?.user.phoneCode} ${registrationInfo?.user.mobileNumber}`}</Label.Detail>
              </El.StyledLabelInfo>
            </Form.Field>}
          </Form.Group>
          {mobileNumberNotPreset && renderNewMobileNumber()}
          {renderPasswordFields()}
          {registrationInfoReset?.tfaType === 'sms' && registrationInfoReset?.mobileNumber && <Form.Field size='large' style={{ marginBottom: '2em' }}>
            <label>{t('Mobile number:')}</label>
            <El.StyledLabelInfo>
              <Icon name='phone' />
              <Label.Detail>{registrationInfoReset?.mobileNumber}</Label.Detail>
            </El.StyledLabelInfo>
          </Form.Field>
          }
          {enableCodeField && renderTwoFa()}
          {settingsData?.files?.length > 0 && registrationInfo && renderCheckTerms()}
        </div>
        {renderActions(enableCodeField, mobileNumberNotPreset)}
        <Divider section style={{ marginBottom: 0 }} />
      </Form>
    );
  };

  return <LayoutAuth isLoading={isBusy}>
    {renderPasswordForm()}
  </LayoutAuth>
}
export default PasswordForm
